import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCatalog } from "@/providers/CatalogProvider";
import {
  useAssessmentEntriesQuery,
  useAssessmentReportQuery,
  useStudentAssessmentsQuery,
  useStudentQuery,
} from "@/hooks/useStudents";
import type { Assessment, AssessmentEntry } from "@/types/assessment";
import type { DomainCode } from "@/constants/domains";
import type { ScaleEntry } from "@/types/scale";
import type { ActivityDefinition } from "@/types/activity";
import { FloatingReportLayout } from "@/components/report/FloatingReportLayout";
import { ReportExportPanel } from "@/components/report/ReportExportPanel";
import { ReportContextRequired } from "@/components/report/ReportContextRequired";
import { ReportSourceError } from "@/components/report/ReportSourceError";
import { VoiceInputButton } from "@/components/common/VoiceInputButton";
import { EChart } from "@/components/charts/EChart";
import { formatDate } from "@/utils/date";
import { useParentReportDraft } from "@/hooks/useParentReportDraft";
import type { ParentReportDraftContentV1 } from "@/services/repositories/report-draft-repository";
import type { VersionedAutosaveStatus } from "@/hooks/useVersionedAutosave";
import {
  assessmentRecordLabel,
  completedAssessmentsForStudent,
  studentWorkspacePaths,
} from "@/utils/student-workspace";

const ALL_DOMAINS: DomainCode[] = ["HE", "SO", "LA", "SC", "AR"];

const DOMAIN_TITLES: Record<DomainCode, string> = {
  HE: "健康领域",
  SO: "社会领域",
  LA: "语言领域",
  SC: "科学领域",
  AR: "艺术领域",
};

const PARENT_REPORT_TOC_ITEMS = [
  { id: "parent-observation", label: "观察记录" },
  { id: "parent-score", label: "评估概况" },
  { id: "parent-strengths", label: "具体表现" },
  { id: "parent-targets", label: "发展目标" },
  { id: "parent-guidance", label: "家庭建议" },
  { id: "parent-activities", label: "活动支持" },
];

interface DomainEntry {
  entry: ScaleEntry;
  record: AssessmentEntry;
}

interface DomainBucket {
  scored2: DomainEntry[];
  scored1: DomainEntry[];
  others: DomainEntry[];
}

interface ActivityTarget {
  entry: ScaleEntry;
  record: AssessmentEntry;
}

export function ParentReportPage(): JSX.Element {
  const { scaleLibrary, activityLibrary } = useCatalog();
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentQuery = useStudentQuery(studentId);
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);

  const [manageHighlights, setManageHighlights] = useState(false);
  const [manageTargets, setManageTargets] = useState(false);
  const [manageActivities, setManageActivities] = useState(false);
  const [draftFeedback, setDraftFeedback] = useState<string>();

  const requestedAssessmentId = searchParams.get("assessmentId") ?? undefined;
  const completedAssessments = completedAssessmentsForStudent(
    assessmentsQuery.data ?? [],
    studentId,
  );
  const selectedSource = requestedAssessmentId
    ? completedAssessments.find((assessment) => assessment.id === requestedAssessmentId)
    : completedAssessments[0];
  const selectedAssessment = useMemo(
    () => (selectedSource?.integrityStatus === "verified" ? selectedSource : undefined),
    [selectedSource],
  );
  const selectedAssessmentId = selectedAssessment?.id;

  useEffect(() => {
    if (!requestedAssessmentId && completedAssessments[0]) {
      const next = new URLSearchParams(searchParams);
      next.set("assessmentId", completedAssessments[0].id);
      setSearchParams(next, { replace: true });
    }
  }, [completedAssessments, requestedAssessmentId, searchParams, setSearchParams]);

  function selectAssessment(assessmentId: string): void {
    const next = new URLSearchParams(searchParams);
    next.set("assessmentId", assessmentId);
    setSearchParams(next);
  }

  const entriesQuery = useAssessmentEntriesQuery(selectedAssessmentId);
  const reportQuery = useAssessmentReportQuery(selectedAssessmentId);
  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);

  const domainBuckets = useMemo(() => {
    return buildDomainBuckets(entries, scaleLibrary.entryIndex);
  }, [entries, scaleLibrary.entryIndex]);

  const domainPercents = useMemo(() => {
    const result: Record<DomainCode, number | undefined> = {
      HE: undefined,
      SO: undefined,
      LA: undefined,
      SC: undefined,
      AR: undefined,
    };
    for (const domain of reportQuery.data?.summary.domains ?? []) {
      if (ALL_DOMAINS.includes(domain.code as DomainCode)) {
        result[domain.code as DomainCode] = domain.scorePercent;
      }
    }
    return result;
  }, [reportQuery.data]);

  const defaultHighlights = useMemo(() => {
    const map: Record<DomainCode, string[]> = {} as Record<DomainCode, string[]>;
    ALL_DOMAINS.forEach((domain) => {
      map[domain] = domainBuckets.get(domain)?.scored2.map((item) => item.entry.code) ?? [];
    });
    return map;
  }, [domainBuckets]);

  const defaultTargets = useMemo(() => {
    const map: Record<DomainCode, string[]> = {} as Record<DomainCode, string[]>;
    ALL_DOMAINS.forEach((domain) => {
      map[domain] = domainBuckets.get(domain)?.scored1.map((item) => item.entry.code) ?? [];
    });
    return map;
  }, [domainBuckets]);

  const activityData = useMemo(() => {
    return buildActivityTargets(entries, activityLibrary.activities, scaleLibrary.entryIndex);
  }, [entries, activityLibrary.activities, scaleLibrary.entryIndex]);

  const defaultActivityTargets = useMemo(() => {
    const map: Record<string, string[]> = {};
    activityData.forEach(({ activity, targets }) => {
      map[activity.code] = targets.map((target) => target.entry.code);
    });
    return map;
  }, [activityData]);

  const defaultDraftContent = useMemo<ParentReportDraftContentV1>(
    () => ({
      observationNote: "",
      scoreNote: "",
      parentSuggestion: "",
      highlightEntryCodesByDomain: defaultHighlights,
      targetEntryCodesByDomain: defaultTargets,
      activityEntryCodesByActivity: defaultActivityTargets,
    }),
    [defaultActivityTargets, defaultHighlights, defaultTargets],
  );

  const {
    content: draftContent,
    updateContent: updateDraftContent,
    flush: flushDraft,
    retry: retryDraft,
    reload: reloadDraft,
    saveStatus: draftSaveStatus,
    isLoading: draftLoading,
    loadError: draftLoadError,
  } = useParentReportDraft({
    assessmentId: selectedAssessmentId,
    defaultContent: defaultDraftContent,
    defaultsReady: !entriesQuery.isLoading && !entriesQuery.isError,
  });

  const parentReportTocItems = useMemo(
    () =>
      PARENT_REPORT_TOC_ITEMS.map((item) => ({
        ...item,
        meta: getParentReportSectionMeta(item.id, draftContent),
      })),
    [draftContent],
  );

  useEffect(() => {
    setManageHighlights(false);
    setManageTargets(false);
    setManageActivities(false);
    setDraftFeedback(undefined);
  }, [selectedAssessmentId]);

  const currentChildName = studentQuery.data?.profile.name ?? "儿童";

  const radarOption = useMemo(() => {
    const values = ALL_DOMAINS.map((domain) => domainPercents[domain] ?? 0);
    if (values.every((value) => value === 0)) {
      return undefined;
    }
    return {
      tooltip: {},
      radar: {
        indicator: ALL_DOMAINS.map((domain) => ({
          name: `${DOMAIN_TITLES[domain]}得分率`,
          max: 100,
        })),
      },
      series: [
        {
          type: "radar",
          data: [
            {
              value: values,
              name: "得分率",
            },
          ],
        },
      ],
    };
  }, [domainPercents]);

  const getHighlightCodes = (domain: DomainCode): string[] => {
    return draftContent?.highlightEntryCodesByDomain[domain] ?? defaultHighlights[domain] ?? [];
  };

  const getTargetCodes = (domain: DomainCode): string[] => {
    return draftContent?.targetEntryCodesByDomain[domain] ?? defaultTargets[domain] ?? [];
  };

  const getActivityCodes = (activityCode: string): string[] => {
    return (
      draftContent?.activityEntryCodesByActivity[activityCode] ??
      defaultActivityTargets[activityCode] ??
      []
    );
  };

  function removeHighlight(domain: DomainCode, entryCode: string) {
    updateDraftContent((previous) => ({
      ...previous,
      highlightEntryCodesByDomain: {
        ...previous.highlightEntryCodesByDomain,
        [domain]: getHighlightCodes(domain).filter((code) => code !== entryCode),
      },
    }));
  }

  function addHighlight(domain: DomainCode, entryCode: string) {
    if (!entryCode) return;
    const next = getHighlightCodes(domain);
    if (next.includes(entryCode)) return;
    updateDraftContent((previous) => ({
      ...previous,
      highlightEntryCodesByDomain: {
        ...previous.highlightEntryCodesByDomain,
        [domain]: [...next, entryCode],
      },
    }));
  }

  function removeTarget(domain: DomainCode, entryCode: string) {
    updateDraftContent((previous) => ({
      ...previous,
      targetEntryCodesByDomain: {
        ...previous.targetEntryCodesByDomain,
        [domain]: getTargetCodes(domain).filter((code) => code !== entryCode),
      },
    }));
  }

  function addTarget(domain: DomainCode, entryCode: string) {
    if (!entryCode) return;
    const next = getTargetCodes(domain);
    if (next.includes(entryCode)) return;
    updateDraftContent((previous) => ({
      ...previous,
      targetEntryCodesByDomain: {
        ...previous.targetEntryCodesByDomain,
        [domain]: [...next, entryCode],
      },
    }));
  }

  function removeActivityTarget(activityCode: string, entryCode: string) {
    updateDraftContent((previous) => ({
      ...previous,
      activityEntryCodesByActivity: {
        ...previous.activityEntryCodesByActivity,
        [activityCode]: getActivityCodes(activityCode).filter((code) => code !== entryCode),
      },
    }));
  }

  function addActivityTarget(activityCode: string, entryCode: string) {
    if (!entryCode) return;
    const next = getActivityCodes(activityCode);
    if (next.includes(entryCode)) return;
    updateDraftContent((previous) => ({
      ...previous,
      activityEntryCodesByActivity: {
        ...previous.activityEntryCodesByActivity,
        [activityCode]: [...next, entryCode],
      },
    }));
  }

  async function handlePrint(): Promise<void> {
    setDraftFeedback(undefined);
    const saved = await flushDraft({ forceCreate: true });
    if (!saved) {
      setDraftFeedback("报告未能保存到服务器，已阻止打印。请重试后再操作。");
      return;
    }
    window.print();
  }

  async function handleRetryDraft(): Promise<void> {
    setDraftFeedback(undefined);
    await retryDraft();
  }

  async function handleReloadDraft(): Promise<void> {
    setDraftFeedback(undefined);
    await reloadDraft();
  }

  if (assessmentsQuery.isLoading || studentQuery.isLoading) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-slate-800">家长报告</h1>
        </header>
        <section className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
          正在读取报告范围…
        </section>
      </div>
    );
  }

  if (requestedAssessmentId && !selectedAssessment) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-slate-800">家长报告</h1>
        </header>
        <ReportSourceError
          studentId={studentId}
          title="无法打开这份家长报告"
          description="该评估不属于当前儿童、尚未完成，或未通过完整性校验。页面未读取或写入任何报告草稿。"
          returnTo={studentWorkspacePaths.parentReport(studentId)}
          returnLabel="返回家长报告"
        />
      </div>
    );
  }

  if (!selectedAssessment) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-slate-800">家长报告</h1>
          <p className="mt-2 text-sm text-slate-500">查看儿童表现并整理家庭活动建议。</p>
        </header>
        <ReportContextRequired reportName="家长报告" studentId={studentId} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="overflow-hidden rounded-[1.75rem] border border-violet-200/80 bg-gradient-to-br from-white via-white to-accent-cool-soft shadow-[0_24px_56px_-38px_rgba(105,86,163,0.5)]">
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_minmax(19rem,30rem)] lg:p-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent-cool">
              家庭沟通
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-800">家长报告</h1>
            <p className="mt-2 text-sm text-slate-500">查看儿童表现并整理家庭活动建议。</p>
            <label className="mt-5 block max-w-xl text-sm font-semibold text-slate-700">
              评估依据
              <select
                value={selectedAssessment.id}
                onChange={(event) => selectAssessment(event.target.value)}
                className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-700 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              >
                {completedAssessments.map((assessment) => (
                  <option key={assessment.id} value={assessment.id}>
                    {formatAssessmentOption(assessment)}
                    {assessment.integrityStatus === "verified" ? "" : " · 尚未校验"}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <section className="rounded-2xl border border-white/90 bg-white/85 p-5 shadow-[0_18px_40px_-30px_rgba(71,85,105,0.55)] backdrop-blur">
            <h2 className="text-sm font-semibold text-slate-800">家长报告草稿</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              填写内容会自动保存到服务器，可确认后导出正式报告。
            </p>
            <div className="mt-5">
              <ReportExportPanel onDownloadPdf={() => void handlePrint()} disabled={draftLoading} />
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <DraftSaveIndicator
                status={draftSaveStatus}
                feedback={draftFeedback}
                loadError={draftLoadError}
                onRetry={() => void handleRetryDraft()}
                onCopy={() => void copyDraftContent(draftContent)}
                onReload={() => void handleReloadDraft()}
              />
            </div>
          </section>
        </div>
      </header>

      <FloatingReportLayout
        items={parentReportTocItems}
        label="家长报告目录"
        showNumbers={false}
        tone="cool"
      >
        <section
          id="parent-observation"
          className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-800">{currentChildName} 的观察记录</h2>
          <div className="relative mt-3">
            <textarea
              value={draftContent?.observationNote ?? ""}
              onChange={(event) =>
                updateDraftContent((previous) => ({
                  ...previous,
                  observationNote: event.target.value,
                }))
              }
              rows={4}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-14 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              placeholder="记录与家长分享的重点观察、课堂互动细节或成长故事。"
            />
            <VoiceInputButton
              size="sm"
              initialValue={draftContent?.observationNote ?? ""}
              onSave={(value) =>
                updateDraftContent((previous) => ({ ...previous, observationNote: value }))
              }
              className="absolute bottom-3 right-3"
            />
          </div>
        </section>

        <section
          id="parent-score"
          className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Child+智慧平台评估得分概况</h2>
              <p className="text-sm text-slate-500">帮助家长快速了解五大领域的整体掌握度。</p>
            </div>
          </div>
          <div className="relative">
            <textarea
              value={draftContent?.scoreNote ?? ""}
              onChange={(event) =>
                updateDraftContent((previous) => ({ ...previous, scoreNote: event.target.value }))
              }
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-14 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              placeholder="简要说明得分概况，如：健康领域表现突出，语言领域持续改善等。"
            />
            <VoiceInputButton
              size="sm"
              initialValue={draftContent?.scoreNote ?? ""}
              onSave={(value) =>
                updateDraftContent((previous) => ({ ...previous, scoreNote: value }))
              }
              className="absolute bottom-3 right-3"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-2 text-left font-medium">测评领域</th>
                  {ALL_DOMAINS.map((code) => (
                    <th key={code} className="px-4 py-2 text-center font-medium">
                      {DOMAIN_TITLES[code]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white text-slate-700">
                  <td className="px-4 py-3 font-medium text-slate-600">得分率</td>
                  {ALL_DOMAINS.map((code) => (
                    <td key={code} className="px-4 py-3 text-center">
                      {domainPercents[code] !== undefined
                        ? `${(domainPercents[code] ?? 0).toFixed(2)}%`
                        : "—"}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
            {radarOption ? (
              <EChart option={radarOption} height={360} />
            ) : (
              <p className="text-sm text-slate-500">暂无足够的得分率数据。</p>
            )}
          </div>
        </section>

        <section
          id="parent-strengths"
          className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-800">儿童各个领域的具体表现</h2>
            <button
              type="button"
              onClick={() => setManageHighlights((prev) => !prev)}
              className="self-start rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-brand hover:text-brand"
            >
              {manageHighlights ? "完成管理" : "管理条目"}
            </button>
          </div>
          <div className="space-y-6">
            {ALL_DOMAINS.map((domain) => {
              const bucket = domainBuckets.get(domain);
              if (!bucket) {
                return null;
              }
              const codes = getHighlightCodes(domain);
              if (codes.length === 0) {
                return null;
              }
              const selectedEntries = bucket.scored2.filter((item) =>
                codes.includes(item.entry.code),
              );
              if (selectedEntries.length === 0) {
                return null;
              }
              const availableEntries = bucket.scored2.filter(
                (item) => !codes.includes(item.entry.code),
              );
              const grouped = groupBySubdomain(selectedEntries);
              return (
                <article key={domain} className="space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-base font-semibold text-slate-700">
                      （{DOMAIN_TITLES[domain]}）
                    </h3>
                    {manageHighlights && availableEntries.length > 0 ? (
                      <select
                        defaultValue=""
                        onChange={(event) => {
                          addHighlight(domain, event.target.value);
                          event.currentTarget.value = "";
                        }}
                        className="w-full rounded border border-dashed border-slate-300 px-3 py-1 text-xs focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-56"
                      >
                        <option value="">+ 添加表现条目</option>
                        {availableEntries.map(({ entry }) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.code} · {entry.shortTermGoal?.title ?? entry.longTermGoal.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  {grouped.map(({ subdomainLabel, items }) => (
                    <div key={subdomainLabel} className="space-y-2 rounded-lg bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-700">【{subdomainLabel}】</p>
                      <ul className="space-y-1 text-sm leading-6 text-slate-600">
                        {items.map(({ entry }) => (
                          <li key={entry.code} className="flex items-start justify-between gap-3">
                            <span>
                              {entry.longTermGoal.title}
                              {entry.shortTermGoal ? ` - ${entry.shortTermGoal.title}` : ""}：
                              {entry.standard}
                            </span>
                            {manageHighlights ? (
                              <button
                                type="button"
                                onClick={() => removeHighlight(domain, entry.code)}
                                className="text-xs text-rose-500 hover:underline"
                              >
                                移除
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </article>
              );
            })}
          </div>
        </section>

        <section
          id="parent-targets"
          className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">儿童的下一阶段的发展目标建议</h2>
              <p className="text-sm text-slate-500">
                结合得分为 1 的条目，便于家长在日常场景中针对性引导与巩固。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setManageTargets((prev) => !prev)}
              className="self-start rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-brand hover:text-brand"
            >
              {manageTargets ? "完成管理" : "管理条目"}
            </button>
          </div>
          <div className="space-y-6">
            {ALL_DOMAINS.map((domain) => {
              const bucket = domainBuckets.get(domain);
              if (!bucket) {
                return null;
              }
              const codes = getTargetCodes(domain);
              if (codes.length === 0) {
                return null;
              }
              const selectedEntries = bucket.scored1.filter((item) =>
                codes.includes(item.entry.code),
              );
              if (selectedEntries.length === 0) {
                return null;
              }
              const availableEntries = bucket.scored1.filter(
                (item) => !codes.includes(item.entry.code),
              );
              const grouped = groupBySubdomain(selectedEntries);
              return (
                <article
                  key={domain}
                  className="space-y-3 rounded-lg border border-dashed border-brand/30 p-4"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-base font-semibold text-slate-700">
                      （{DOMAIN_TITLES[domain]}）
                    </h3>
                    {manageTargets && availableEntries.length > 0 ? (
                      <select
                        defaultValue=""
                        onChange={(event) => {
                          addTarget(domain, event.target.value);
                          event.currentTarget.value = "";
                        }}
                        className="w-full rounded border border-dashed border-slate-300 px-3 py-1 text-xs focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-56"
                      >
                        <option value="">+ 添加发展目标</option>
                        {availableEntries.map(({ entry }) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.code} · {entry.shortTermGoal?.title ?? entry.longTermGoal.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  {grouped.map(({ subdomainLabel, items }) => (
                    <div key={subdomainLabel} className="space-y-1">
                      <p className="text-sm font-semibold text-slate-700">【{subdomainLabel}】</p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                        {items.map(({ entry }) => (
                          <li key={entry.code} className="flex items-start justify-between gap-3">
                            <span>
                              {entry.shortTermGoal?.title ?? entry.longTermGoal.title}：
                              {entry.standard}
                            </span>
                            {manageTargets ? (
                              <button
                                type="button"
                                onClick={() => removeTarget(domain, entry.code)}
                                className="text-xs text-rose-500 hover:underline"
                              >
                                移除
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </article>
              );
            })}
          </div>
        </section>

        <section
          id="parent-guidance"
          className="scroll-mt-[11.5rem] rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-semibold text-slate-800">亲子互动和亲子游戏的小建议</h2>
          <div className="relative mt-3">
            <textarea
              value={draftContent?.parentSuggestion ?? ""}
              onChange={(event) =>
                updateDraftContent((previous) => ({
                  ...previous,
                  parentSuggestion: event.target.value,
                }))
              }
              rows={4}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-14 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              placeholder="为家长提供 2-3 条具体可执行的互动方式、游戏或日常练习建议。"
            />
            <VoiceInputButton
              size="sm"
              initialValue={draftContent?.parentSuggestion ?? ""}
              onSave={(value) =>
                updateDraftContent((previous) => ({ ...previous, parentSuggestion: value }))
              }
              className="absolute bottom-3 right-3"
            />
          </div>
        </section>

        <section
          id="parent-activities"
          className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-800">活动中的发展目标与支持策略</h2>
            <button
              type="button"
              onClick={() => setManageActivities((prev) => !prev)}
              className="self-start rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-brand hover:text-brand"
            >
              {manageActivities ? "完成管理" : "管理条目"}
            </button>
          </div>
          <div className="space-y-4">
            {activityData.map(({ activity, targets }) => {
              if (targets.length === 0) {
                return null;
              }
              const selectedCodes = getActivityCodes(activity.code);
              const selectedTargets = targets.filter((target) =>
                selectedCodes.includes(target.entry.code),
              );
              if (selectedTargets.length === 0) {
                return null;
              }
              const availableTargets = targets.filter(
                (target) => !selectedCodes.includes(target.entry.code),
              );
              return (
                <article key={activity.code} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-slate-800">{activity.name}</h3>
                      <span className="text-xs text-slate-500">
                        关注目标 {selectedTargets.length} 条
                      </span>
                    </div>
                    {manageActivities && availableTargets.length > 0 ? (
                      <select
                        defaultValue=""
                        onChange={(event) => {
                          addActivityTarget(activity.code, event.target.value);
                          event.currentTarget.value = "";
                        }}
                        className="w-full rounded border border-dashed border-slate-300 px-3 py-1 text-xs focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-64"
                      >
                        <option value="">+ 添加活动目标</option>
                        {availableTargets.map((target) => (
                          <option key={target.entry.code} value={target.entry.code}>
                            {target.entry.code} ·{" "}
                            {target.entry.shortTermGoal?.title ?? target.entry.longTermGoal.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  <table className="mt-3 w-full divide-y divide-slate-200 text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600">
                        <th className="px-3 py-2 text-left font-medium">目标</th>
                        <th className="px-3 py-2 text-left font-medium">活动示例 / 支持策略</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTargets.map((target) => (
                        <tr key={target.entry.code} className="bg-white text-slate-700">
                          <td className="px-3 py-2 align-top text-sm font-semibold text-slate-700">
                            {target.entry.shortTermGoal?.title ?? target.entry.longTermGoal.title}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600">
                            <div className="flex items-start justify-between gap-3">
                              <span>{target.entry.examples || target.entry.standard}</span>
                              {manageActivities ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeActivityTarget(activity.code, target.entry.code)
                                  }
                                  className="text-xs text-rose-500 hover:underline"
                                >
                                  移除
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </article>
              );
            })}
          </div>
        </section>
      </FloatingReportLayout>
    </div>
  );
}

function formatAssessmentOption(assessment: Assessment): string {
  return `${formatDate(assessment.startedAt)} · ${assessmentRecordLabel()}`;
}

function getParentReportSectionMeta(
  sectionId: string,
  content: ParentReportDraftContentV1 | undefined,
): string {
  if (!content) {
    return "未填写";
  }

  switch (sectionId) {
    case "parent-observation":
      return content.observationNote.trim() ? "已填写" : "未填写";
    case "parent-score":
      return content.scoreNote.trim() ? "已填写" : "未填写";
    case "parent-strengths":
      return hasSelectedEntries(content.highlightEntryCodesByDomain) ? "已配置" : "未配置";
    case "parent-targets":
      return hasSelectedEntries(content.targetEntryCodesByDomain) ? "已配置" : "未配置";
    case "parent-guidance":
      return content.parentSuggestion.trim() ? "已填写" : "未填写";
    case "parent-activities":
      return hasSelectedEntries(content.activityEntryCodesByActivity) ? "已配置" : "未配置";
    default:
      return "未填写";
  }
}

function hasSelectedEntries(selections: Record<string, string[]>): boolean {
  return Object.values(selections).some((codes) => codes.length > 0);
}

function DraftSaveIndicator({
  status,
  feedback,
  loadError,
  onRetry,
  onCopy,
  onReload,
}: {
  status: VersionedAutosaveStatus;
  feedback?: string;
  loadError: unknown;
  onRetry: () => void;
  onCopy: () => void;
  onReload: () => void;
}): JSX.Element {
  const state = getDraftSaveState(status);
  const message = feedback ?? (loadError ? "读取服务器草稿失败，请稍后重试。" : state.label);
  return (
    <div className="flex flex-wrap items-center gap-3" aria-live="polite">
      {message ? (
        <p className={`text-sm ${feedback || loadError ? "text-red-600" : state.className}`}>
          {message}
        </p>
      ) : null}
      {status === "error" || status === "conflict" ? (
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-semibold text-brand hover:underline"
        >
          重试保存
        </button>
      ) : null}
      {status === "conflict" ? (
        <>
          <button
            type="button"
            onClick={onCopy}
            className="text-sm font-semibold text-brand hover:underline"
          >
            复制当前内容
          </button>
          <button
            type="button"
            onClick={onReload}
            className="text-sm font-semibold text-brand hover:underline"
          >
            重新载入服务器版本
          </button>
        </>
      ) : null}
    </div>
  );
}

async function copyDraftContent(content: ParentReportDraftContentV1 | undefined): Promise<void> {
  if (content && navigator.clipboard) {
    await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
  }
}

function getDraftSaveState(status: VersionedAutosaveStatus) {
  switch (status) {
    case "dirty":
      return { label: "有更改尚未保存", className: "text-amber-700" };
    case "saving":
      return { label: "正在保存…", className: "text-sky-700" };
    case "saved":
      return { label: "已保存到服务器", className: "text-brand-dark" };
    case "error":
      return { label: "保存失败，当前内容仍保留在页面", className: "text-red-600" };
    case "conflict":
      return {
        label: "其他位置已更新，当前内容尚未保存",
        className: "text-red-600",
      };
    default:
      return { label: "", className: "text-slate-500" };
  }
}

function buildDomainBuckets(
  entries: AssessmentEntry[],
  entryIndex: Map<string, ScaleEntry>,
): Map<DomainCode, DomainBucket> {
  const buckets = new Map<DomainCode, DomainBucket>();
  entries.forEach((record) => {
    const entry = entryIndex.get(record.entryCode);
    if (!entry) {
      return;
    }
    if (!buckets.has(entry.domainCode)) {
      buckets.set(entry.domainCode, { scored2: [], scored1: [], others: [] });
    }
    const bucket = buckets.get(entry.domainCode)!;
    const unit = { entry, record };
    if (record.score === 2) {
      bucket.scored2.push(unit);
    } else if (record.score === 1) {
      bucket.scored1.push(unit);
    } else {
      bucket.others.push(unit);
    }
  });
  return buckets;
}

function groupBySubdomain(entries: DomainEntry[]) {
  const map = new Map<string, DomainEntry[]>();
  entries.forEach((item) => {
    const label = item.entry.subdomainLabel;
    if (!map.has(label)) {
      map.set(label, []);
    }
    map.get(label)!.push(item);
  });
  return Array.from(map.entries()).map(([subdomainLabel, items]) => ({
    subdomainLabel,
    items,
  }));
}

function buildActivityTargets(
  entries: AssessmentEntry[],
  activities: ActivityDefinition[],
  entryIndex: Map<string, ScaleEntry>,
) {
  const recordMap = new Map(entries.map((entry) => [entry.entryCode, entry]));
  return activities.map((activity) => {
    const codes = new Set<string>();
    activity.modules.forEach((module) => {
      module.steps.forEach((step) => {
        step.entryCodes.forEach((code) => {
          const record = recordMap.get(code);
          if (record && record.score === 1) {
            codes.add(code);
          }
        });
      });
    });
    const targets: ActivityTarget[] = Array.from(codes)
      .map((code) => {
        const entry = entryIndex.get(code);
        const record = recordMap.get(code);
        if (!entry || !record) {
          return null;
        }
        return { entry, record };
      })
      .filter((item): item is ActivityTarget => Boolean(item));
    return {
      activity,
      targets,
    };
  });
}
